import { useEffect, useState } from "react";
import { api, ROLE_LABEL, fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, KeyRound } from "lucide-react";

const EMPTY = { email: "", vorname: "", nachname: "", rolle: "employee", passwort: "" };

export default function Nutzer() {
  const [list, setList] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [resetInfo, setResetInfo] = useState(null);

  const load = async () => {
    const r = await api.get("/users");
    setList(r.data);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/users", form);
      toast.success("Nutzer angelegt.");
      setDialogOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehlgeschlagen."); }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { is_active: !u.is_active });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehlgeschlagen."); }
  };

  const resetPw = async (u) => {
    if (!window.confirm(`Passwort für ${u.email} zurücksetzen?`)) return;
    try {
      const r = await api.post(`/users/${u.id}/reset-password`);
      setResetInfo({ email: u.email, pw: r.data.new_passwort });
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehlgeschlagen."); }
  };

  const changeRole = async (u, rolle) => {
    try { await api.patch(`/users/${u.id}`, { rolle }); toast.success("Rolle geändert."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Fehlgeschlagen."); }
  };

  return (
    <div className="space-y-6" data-testid="nutzer-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Nutzer</h1>
          <p className="text-sm text-slate-500 mt-1">Benutzerkonten und Rollen verwalten.</p>
        </div>
        <Button onClick={() => { setForm(EMPTY); setDialogOpen(true); }} data-testid="nutzer-add-button"><Plus className="w-4 h-4 mr-2" /> Nutzer anlegen</Button>
      </div>
      <Card className="border-slate-200">
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">E-Mail</th>
                <th className="px-4 py-3">Rolle</th>
                <th className="px-4 py-3">Aktiv</th>
                <th className="px-4 py-3">Erstellt</th>
                <th className="px-4 py-3 text-right">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {list.map((u) => (
                <tr key={u.id} className="border-b border-slate-100 last:border-0" data-testid={`nutzer-row-${u.email}`}>
                  <td className="px-4 py-3 font-medium text-slate-900">{u.vorname} {u.nachname}</td>
                  <td className="px-4 py-3 text-slate-700">{u.email}</td>
                  <td className="px-4 py-3">
                    <Select value={u.rolle} onValueChange={(v) => changeRole(u, v)}>
                      <SelectTrigger className="h-8 w-40" data-testid={`nutzer-rolle-${u.email}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(ROLE_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-3"><Switch checked={u.is_active} onCheckedChange={() => toggleActive(u)} data-testid={`nutzer-active-${u.email}`} /></td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{fmtDateTime(u.created_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <Button size="sm" variant="outline" onClick={() => resetPw(u)} data-testid={`nutzer-reset-${u.email}`}><KeyRound className="w-4 h-4 mr-2" /> Passwort</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="nutzer-dialog">
          <DialogHeader><DialogTitle>Neuen Nutzer anlegen</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Vorname</Label><Input value={form.vorname} onChange={(e) => setForm({ ...form, vorname: e.target.value })} data-testid="nutzer-vorname-input" /></div>
              <div><Label>Nachname</Label><Input value={form.nachname} onChange={(e) => setForm({ ...form, nachname: e.target.value })} data-testid="nutzer-nachname-input" /></div>
            </div>
            <div><Label>E-Mail</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="nutzer-email-input" /></div>
            <div>
              <Label>Rolle</Label>
              <Select value={form.rolle} onValueChange={(v) => setForm({ ...form, rolle: v })}>
                <SelectTrigger data-testid="nutzer-rolle-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(ROLE_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Initial-Passwort</Label><Input type="text" minLength={6} value={form.passwort} onChange={(e) => setForm({ ...form, passwort: e.target.value })} data-testid="nutzer-passwort-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
            <Button onClick={create} data-testid="nutzer-save-button">Anlegen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetInfo} onOpenChange={(o) => !o && setResetInfo(null)}>
        <DialogContent data-testid="reset-info-dialog">
          <DialogHeader><DialogTitle>Neues Passwort</DialogTitle></DialogHeader>
          <div className="space-y-2 text-sm">
            <div>Das Passwort wird nur einmal angezeigt. Bitte notieren und an den Nutzer weitergeben.</div>
            <div className="p-3 bg-slate-100 rounded font-mono text-slate-900" data-testid="reset-info-pw">{resetInfo?.pw}</div>
            <div className="text-slate-500">für: {resetInfo?.email}</div>
          </div>
          <DialogFooter><Button onClick={() => setResetInfo(null)}>Schließen</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
